import React, { useState } from 'react';
import { Form, Input, Button, Tag, message, Divider, Select } from 'antd';
import ProjectServices from '../../services/projectServices';
import { getTranslation } from '../../util/utilFunc';
import { useSelector } from 'react-redux';
const { Option } = Select;
const { TextArea } = Input;

const availableTags = [
  '🎨 UI',
  '🔥 Performance',
  '🔗 Integration',
  '🐞Bug',
  'Enhancement 🪄',
  'UX 🖼️',
  'Security 🔐',
  'Must Have 🎯',
  'Good To Have 👍'
];

const FeatureRequestForm = ({ closeDrawer, fetchFeatures }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const { language } = useSelector((state) => state.auth);


  // Function to handle tag selection and de-selection
  const handleTagClick = (tag) => {
    setSelectedTags((prevSelectedTags) => {
      if (prevSelectedTags.includes(tag)) {
        // If the tag is already selected, remove it
        return prevSelectedTags.filter((selectedTag) => selectedTag !== tag);
      } else {
        // If the tag is not selected, add it
        return [...prevSelectedTags, tag];
      }
    });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await ProjectServices.createFeatureRequest({
        title: values.title,
        description: values.description,
        additional_details: { tags: selectedTags, category: values.category }
      });
      if (response.data && response.status === 201) {
        message.success(getTranslation("Feature request submitted successfully!", language));
        closeDrawer();
        fetchFeatures();
      }
    } catch (error) {
      console.log(error);
      message.error(getTranslation("Error submitting the feature request!", language));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>{getTranslation("Feature Request", language)}</h2>
      <Divider />
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label={getTranslation("Category", language)}
          name="category"
          rules={[
            {
              required: true,
              message: getTranslation("Please select a category!", language),
            },
          ]}
        >
          <Select placeholder={getTranslation("Select a category", language)}>
            <Option value="feature_request">
              {getTranslation("Feature Request", language)}
            </Option>
            <Option value="bug_or_issue">
              {getTranslation("Bug or Issue", language)}
            </Option>
            <Option value="user_experience_improvement">
              {getTranslation("User Experience Improvement", language)}
            </Option>
            <Option value="small_enhancement">
              {getTranslation("Small Enhancement", language)}
            </Option>
            <Option value="big_idea">
              {getTranslation("Big Ideas", language)}
            </Option>
            <Option value="tool_integration">
              {getTranslation("Tool Integration", language)}
            </Option>
            <Option value="performance_improvement">
              {getTranslation("Performance Improvement", language)}
            </Option>
            <Option value="learning_support">
              {getTranslation("Learning and Support", language)}
            </Option>
            <Option value="team_collaboration">
              {getTranslation("Team Collaboration", language)}
            </Option>
            <Option value="other_suggestion">
              {getTranslation("Other Suggestions", language)}
            </Option>
          </Select>
        </Form.Item>

        <Form.Item
          label={getTranslation("Title", language)}
          name="title"
          rules={[
            {
              required: true,
              message: getTranslation("Please enter a title!", language),
            },
          ]}
        >
          <Input
            placeholder={getTranslation(
              "Describe the feature briefly (e.g., 'Add calendar integration')",
              language
            )}
          />
        </Form.Item>

        <Form.Item
          label={getTranslation("Description", language)}
          name="description"
          rules={[
            {
              required: true,
              message: getTranslation("Please provide a description!", language),
            },
          ]}
        >
          <TextArea
            placeholder={getTranslation(
              "Explain how this feature would work and why it would benefit users.",
              language
            )}
            rows={4}
          />
        </Form.Item>

        <Form.Item label={getTranslation("Tags", language)} name="tags">
          <div style={{ marginBottom: "16px" }}>
            {availableTags.map((tag) => (
              <Tag
                key={tag}
                style={{
                  cursor: "pointer",
                  border: selectedTags.includes(tag)
                    ? "2px solid #1890ff"
                    : "1px solid #d9d9d9",
                  borderRadius: "5px",
                  padding: "5px 10px",
                  marginBottom: "8px",
                }}
                color={selectedTags.includes(tag) ? "blue" : "default"}
                onClick={() => handleTagClick(tag)}
              >
                {tag}
              </Tag>
            ))}
          </div>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            {getTranslation("Submit Request", language)}
          </Button>
        </Form.Item>
      </Form>
    </div>

  );
};

export default FeatureRequestForm;
